<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import { useAuthUser } from '@compositions/Auth';
/***/
import { useApplicationStore } from '@stores/application';
import { useAuthReturnTaskStore } from '@stores/auth/returnTask';

// Composants
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const ReturnModal = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Task/Return/ReturnModal'));
const RatingModal = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Task/Rating/RatingModal'));

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Identifiant public de la tâche
const taskPublicId = route.params.taskPublicId;

// Store
const applicationStore = useApplicationStore();
const returnTaskStore = useAuthReturnTaskStore();

// Retourne l'application courante
const currentApplication = computed(() => applicationStore.getCurrent);

// Retourne la tâche correspondant à l'identifiant public dans l'URL
const task = computed(() => {
    return returnTaskStore.getReturnTaskByPublicId(taskPublicId);
});

// Composition pour l'utilisateur connecté
const { authUser, authUserHasPermission } = useAuthUser({});

// Vrai si l'utilisateur est autorisée à accéder à la page
const allowed = ref(false);

// Chargement de la tâche
const loadingTask = ref(true);
const loadingError = ref(false);
const loadTask = (() => {
    returnTaskStore.loadReturnTaskByPublicId(taskPublicId).then(() => {

        if(! task.value.canDoReturn) {
            return Promise.reject();
        }

        loadingTask.value = false;
        allowed.value = true;
        document.title = t('page.me.task.return.title', {
            task: task.value.title
        }) + ' - ' + currentApplication.value.name;

    }).catch((e) => {
        loadingError.value = true;
        loadingTask.value = false;
    });
});

onMounted(() => {
    const canConsult = (authUser.value.type === 'CUSTOMER' && authUserHasPermission('edit task'));
    if(! canConsult) {
        router.push({
            name: 'error',
            params: {
                statusCode: 404
            }
        });
    } else {
        loadTask();
    }
});

// Redirige vers la page d'accueil
/**
 * Redirige vers la page d'accueil
 * @param object query Paramètres à ajouter en GET
 * @return void
 */
const goToHome = ((query) => {
    router.push({
        name: 'home',
        query: query
    });
});


// Méthode exécutée après l'enregistrement d'un retour
const onReturnSaved = (() => {
    // Redirige vers la page d'accueil avec un GET l'identifiant public de la tâche dont on a enregistré le retour
    if(! task.value.canRating) {
        goToHome({
            'from-task-return': task.value.publicId
        });
    }
});
</script>

<template>
    <loader v-if="loadingTask"></loader>
    <layout>
        <p
            v-if="loadingError"
            class="generic-message generic-message-error"
        >
            {{ $t('page.me.task.return.loadingError') }}
        </p>
        <template v-else-if="task">
            <return-modal
                v-if="task.canDoReturn"
                :task="task"
                v-on:return-saved="onReturnSaved()"
                v-on:close="goToHome()"
            ></return-modal>

            <rating-modal
                v-else-if="task.canRating"
                :task="task"
                v-on:rating="goToHome({
                    'from-task-return': task.publicId
                })"
                v-on:close="goToHome()"
            ></rating-modal>
        </template>
    </layout>
</template>
